import { axiosInstance } from './instance';

export interface DistanceMatrixResult {
  distanceMeters: number;
  distanceKm: number;
  distanceText: string;
  durationSeconds: number;
  durationMinutes: number;
  durationText: string;
}

/**
 * Format distance based on value
 * If less than 1000 meters, return in meters, otherwise in km
 * @param meters - Distance in meters
 * @returns Formatted distance object with value, unit, and text
 */
export const formatDistance = (meters: number): { value: number; unit: string; text: string } => {
  if (meters < 1000) {
    return {
      value: meters,
      unit: 'm',
      text: `${meters} m`,
    };
  }
  const km = Number((meters / 1000).toFixed(2)); // Round to 2 decimal places
  return {
    value: km,
    unit: 'km',
    text: `${km} km`,
  };
};

/**
 * Format duration based on value in seconds
 * Returns appropriate unit (seconds, minutes, hours, days) based on value
 * @param seconds - Duration in seconds
 * @returns Formatted duration object with value, unit, and text
 */
export const formatDuration = (seconds: number): { value: number; unit: string; text: string } => {
  if (seconds < 60) {
    // Less than 1 minute - show in seconds
    return {
      value: seconds,
      unit: 'sec',
      text: `${seconds} sec`,
    };
  }
  
  if (seconds < 3600) {
    // Less than 1 hour - show in minutes
    const minutes = Math.ceil(seconds / 60);
    return {
      value: minutes,
      unit: 'min',
      text: `${minutes} min`,
    };
  }
  
  if (seconds < 86400) {
    // Less than 1 day - show in hours and minutes
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.ceil((seconds % 3600) / 60);
    
    if (remainingMinutes === 0) {
      return {
        value: hours,
        unit: 'hr',
        text: `${hours} hr${hours > 1 ? 's' : ''}`,
      };
    }
    
    return {
      value: hours,
      unit: 'hr',
      text: `${hours} hr${hours > 1 ? 's' : ''} ${remainingMinutes} min`,
    };
  }
  
  // 1 day or more - show in days, hours, and minutes
  const days = Math.floor(seconds / 86400);
  const remainingHours = Math.floor((seconds % 86400) / 3600);
  const remainingMinutes = Math.ceil((seconds % 3600) / 60);
  
  let text = `${days} day${days > 1 ? 's' : ''}`;
  
  if (remainingHours > 0) {
    text += ` ${remainingHours} hr${remainingHours > 1 ? 's' : ''}`;
  }
  
  if (remainingMinutes > 0) {
    text += ` ${remainingMinutes} min`;
  }
  
  return {
    value: days,
    unit: 'day',
    text,
  };
};

/**
 * Calculate distance and duration between two locations using the backend API
 * @param originLat - Origin latitude
 * @param originLng - Origin longitude
 * @param destLat - Destination latitude
 * @param destLng - Destination longitude
 * @returns Distance and duration information
 */
export const getDistanceMatrix = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number
): Promise<DistanceMatrixResult> => {
  try {
    const response = await axiosInstance.post('/maps/distance-matrix', {
      originLocation: {
        lat: originLat.toString(),
        lng: originLng.toString(),
      },
      destinationLocation: {
        lat: destLat.toString(),
        lng: destLng.toString(),
      },
    });

    const { distanceMeters, duration } = response.data;
    
    // Format distance: if less than 1000m, show in meters; else in km
    const formattedDistance = formatDistance(distanceMeters);
    
    // Format duration: convert seconds to appropriate unit
    const formattedDuration = formatDuration(duration);

    return {
      distanceMeters,
      distanceKm: formattedDistance.unit === 'km' ? formattedDistance.value : distanceMeters / 1000,
      distanceText: formattedDistance.text,
      durationSeconds: duration,
      durationMinutes: Math.ceil(duration / 60),
      durationText: formattedDuration.text,
    };
  } catch (error: any) {
    console.error('Error fetching distance matrix:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to calculate distance';
      throw new Error(message);
    }
    throw error;
  }
};

